import React, { useRef, useState, useMemo } from 'react';
import ReactECharts, { EChartsInstance } from 'echarts-for-react';
import { DataZoomComponentOption, EChartsOption, SeriesOption, YAXisComponentOption } from 'echarts';

// Enums
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';

// Utils
import getTempByMeasurement from '../../utils/getTempByMeasurement';
import { generateTimeAxisValues, generateDayAxisValues, subsampleAxisValues } from '../../utils/chart-utils';
import { getDefaultDataZoom, getLttbData } from './helpers';
import { ComponentPropsV2, Coordinate, CycleComponents, DataEvent, ParameterGetOptionFn, ParameterGetVisibleDataFn } from './types';
import {
  CHARACTER_SEPARATOR_DEVICE_NAME,
  CHARACTER_SEPARATOR_IOT_KEY_NAME,
  CHART_INDENTS,
  CLASS_NAME_SPACE,
  DATA_ZOOM_ID,
  DEFAULT_Y_AXIS_ZOOM_SLIDER_MARGIN_BY_CELSIUS,
  DEVICE_NAME,
  SET_POINT_NAME,
  X_AXIS,
  NOTE_ID,
  IOT_DEVICE_TYPE,
  AXIS_CONFIG,
} from './constants';
import { DateFormatter } from '../../utils/date-formatter';
import { DateFormats } from '../../enums/date-formats';
import ListLegendsCycle from '../ListLegendsCycle/ListLegendsCycle';
import applyYAxisPadding from '../../utils/applyYAxisPadding';
import { useTranslation } from 'react-i18next';
import { CycleReportItem } from '../../store/models/reports';
import { escapeHtml } from '../../utils/escape-html';

const CycleChartPreviewV2: React.FC<ComponentPropsV2> = ({
  cycleComponents,
  createNote,
  editNote,
  measurement,
  isNoteView,
  chartPreference,
  cycleControllersName,
  setDataZoom,
  nameDevices,
  chartRef,
  generateNoteName,
  cycleReportItems
}) => {
  const { t } = useTranslation();

  const isLoading = useMemo(() => {
    return !(cycleComponents && cycleComponents.source.length > 0);
  }, [cycleComponents]);

  const getListIotDevicesToHide = (list: CycleReportItem[]): string[] => {
    const excludedItems = list.filter((listItems) => listItems.excluded);
    return excludedItems.map((excludedItem) => {
      const { controller_serial_number, type, itemNumber } = excludedItem;
      const iotDeviceType = IOT_DEVICE_TYPE[type];
      return `${controller_serial_number}${CHARACTER_SEPARATOR_IOT_KEY_NAME}${iotDeviceType}${CHARACTER_SEPARATOR_DEVICE_NAME}${itemNumber}`;
    })
  }

  const getColorsByIotDevices = (list: CycleReportItem[]) => {
    return list.reduce((accumulator, listItem) => {
      const { controller_serial_number, type, itemNumber } = listItem;
      const iotDeviceType = IOT_DEVICE_TYPE[type];

      const iotDevice = `${controller_serial_number}${CHARACTER_SEPARATOR_IOT_KEY_NAME}${iotDeviceType}${CHARACTER_SEPARATOR_DEVICE_NAME}${itemNumber}`;
      accumulator[iotDevice] = listItem.color;
      return accumulator;
    }, {})
  }

  const channelsToHide = (cycleReportItems && cycleReportItems.length > 0) ? getListIotDevicesToHide(cycleReportItems) : [];
  const colorsByIotDevices = (cycleReportItems && cycleReportItems.length > 0) ? getColorsByIotDevices(cycleReportItems) : {};

  const defaultYAxisZoomSliderMargin: number = getTempByMeasurement(measurement, DEFAULT_Y_AXIS_ZOOM_SLIDER_MARGIN_BY_CELSIUS);

  const dataEvent = useRef<DataEvent | null>({});

  const [legendData, setLegendData] = useState({ scrollDataIndex: null, selected: null, });
  const [currentYear, setCurrentYear] = useState<number | null>(null);

  const formatYearDayMonthShort = (timestamp) => {
    const date = new Date(timestamp);
    return DateFormatter({ date, format: DateFormats['dd-MMM yyyy'] })
  }

  const getSerIdWithColor = (): { name: string, color: string }[] => {
    const chartInstance = chartRef?.current?.getEchartsInstance();
    if (!chartInstance) return [];

    const series = chartInstance.getOption().series as SeriesOption[];

    return series.reduce((acc, ser) => {
      const isNote = String(ser.name).startsWith(NOTE_ID);

      if (ser.id === SET_POINT_NAME || !ser.color || isNote) return acc;

      const name = ser.id as string;
      const color = (Array.isArray(ser.color) ? ser.color[0] : ser.color) as string;

      acc.push(({ name, color }));
      return acc;
    }, [])
  }

  /*========================Options===============================*/
  const getVisibleData = ({ startPercent, endPercent }: ParameterGetVisibleDataFn) => {
    const dataLength = cycleComponents.source.length;
    const startIndex = Math.floor((startPercent / 100) * dataLength);
    const endIndex = Math.floor((endPercent / 100) * dataLength);
    const slicedData = cycleComponents.source.slice(startIndex, endIndex);

    const result = getLttbData(slicedData.length, slicedData);

    return result;
  }

  const generateTemperatureArray = (convertedMinTemp, convertedMaxTemp, temperature_grid_size, currentDataZoom) => {
    const result = [];

    const yZoomData = Array.isArray(currentDataZoom)
      ? currentDataZoom?.find((item) => item.id === DATA_ZOOM_ID.SLIDER_Y)
      : null;

    const minTemp = yZoomData && yZoomData.startValue || convertedMinTemp
    const maxTemp = yZoomData && yZoomData.endValue || convertedMaxTemp

    result.push(Math.ceil(minTemp));

    let start = Math.ceil(minTemp / temperature_grid_size) * temperature_grid_size;
    if (start < minTemp) start += temperature_grid_size;

    for (let temp = start; temp <= maxTemp; temp += temperature_grid_size) {
      result.push(temp || 0)
    }

    if (result[result.length - 1] !== maxTemp) {
      result.push(Math.floor(maxTemp));
    }

    return result;
  }

  const getOptions = ({ deviceSeriesData, noteSeriesData, currentDataZoom }: ParameterGetOptionFn): EChartsOption => {
    const cycleMeasurementSymbol =
      measurement === CycleMeasurement.Celsius ? CycleMeasurementSymbol.Celsius : CycleMeasurementSymbol.Fahrenheit;

    const minTemp = chartPreference.min_temperature;
    const maxTemp = chartPreference.max_temperature;

    const convertedMinTemp = getTempByMeasurement(measurement, minTemp);
    const convertedMaxTemp = getTempByMeasurement(measurement, maxTemp);

    const adjustedStartZoomY = convertedMinTemp - defaultYAxisZoomSliderMargin;
    const adjustedEndZoomY = convertedMaxTemp + defaultYAxisZoomSliderMargin;

    const clampedStartZoomY = adjustedStartZoomY < convertedMinTemp ? convertedMinTemp : adjustedStartZoomY;
    const clampedEndZoomY = adjustedEndZoomY > convertedMaxTemp ? convertedMaxTemp : adjustedEndZoomY;

    const dataZoom = currentDataZoom ? currentDataZoom : getDefaultDataZoom(clampedStartZoomY, clampedEndZoomY);
    const currentSliderXOptions = dataZoom.find((el) => el.id === DATA_ZOOM_ID.SLIDER_X);

    const visibleDataSource = getVisibleData({
      startPercent: currentSliderXOptions.start,
      endPercent: currentSliderXOptions.end
    })

    let max: number;
    let min: number;

    const deviceSeries: SeriesOption[] = deviceSeriesData.map((item) => {
      const { name, id } = item;
      const color = colorsByIotDevices[name];

      const option: SeriesOption = {
        name,
        id,
        type: 'line',
        symbol: 'none',
        clip: true,
        connectNulls: true,
        color,
        data: visibleDataSource.map((d) => {
          const time = d.time;
          
          // Calculate min/max from data
          if (max === undefined) {
            max = time;
            min = time;
          } else {
            if (time > max) max = time;
            if (time < min) min = time;
          }
          
          return [d.time, d[item.id]];
        })
      };

      return option;
    });

    const noteSeries: SeriesOption[] = noteSeriesData.map((item, index) => {
      const defaultLabel = {
        show: true,
        position: 'top' as const,
        formatter: `#${index + 1}`,
        fontSize: 14,
        borderWidth: 1,
        width: 48,
        lineHeight: 16,
        padding: [0, 4],
        align: 'center' as const,
        borderRadius: 0,
        backgroundColor: 'transparent',
        overflow: 'truncate' as const,
      };

      const noteViewLabel = {
        show: true,
        position: 'top' as const,
        formatter: `#${index + 1}\n${item.name}`,
        fontSize: 16,
        color: '#000',
        borderColor: '#000',
        borderType: 'solid' as const,
        borderWidth: 1,
        width: 110,
        lineHeight: 24,
        padding: 20,
        borderRadius: 6,
        backgroundColor: 'white',
        overflow: 'truncate' as const,
      };

      const label = isNoteView ? noteViewLabel : defaultLabel;

      const markPointData = [
        {
          name: 'coordinate',
          occurrenceId: 'root',
          coord: item.media?.coord,
          label,
        },
        ...(item.links ?? []).map((link) => ({
          name: 'coordinate',
          occurrenceId: link.id,
          coord: link.coord,
          label,
        })),
      ];

      const noteSerOption: SeriesOption = {
        id: item.id,
        name: generateNoteName(item.name),
        encode: { x: 'time', y: item.id },
        type: 'line',
        connectNulls: false,
        clip: true,
        tooltip: {
          show: false
        },
        symbol: 'none',
        lineStyle: {
          opacity: 0
        },
        markPoint: {
          symbol: 'diamond',
          symbolSize: 12,
          itemStyle: {
            color: '#EF8541',
          },
          data: markPointData,
        },
      }

      return noteSerOption;
    })

    const maxSpSeries: SeriesOption[] =
      [{
        name: SET_POINT_NAME,
        id: SET_POINT_NAME,
        type: 'line',
        symbol: 'none',
        clip: true,
        connectNulls: true,
        color: '#0000FF',
        lineStyle: {
          type: 'dashed',
        },
        data: visibleDataSource.map((d) => [d.time, d['sp']])
      }];

    const chartSeriesXAxisVisible: SeriesOption[] = [...deviceSeries, ...noteSeries].map((item: SeriesOption) => ({ ...item, ...X_AXIS.BOTTOM.SERIES_OPTIONS })) as SeriesOption[];
    const chartSeriesXAxisTwoInvisible: SeriesOption[] = [...deviceSeries, ...noteSeries].map((item) => ({ ...item, id: `${item.id}-${X_AXIS.TOP.ID}`, ...X_AXIS.TOP.SERIES_OPTIONS, })) as SeriesOption[];
    const series: SeriesOption[] = [...chartSeriesXAxisVisible, ...chartSeriesXAxisTwoInvisible, ...maxSpSeries];

    const customValues = generateTemperatureArray(convertedMinTemp, convertedMaxTemp, chartPreference.temperature_grid_size, currentDataZoom)

    const yAxis: YAXisComponentOption = {
      type: 'value',
      scale: true,
      min: convertedMinTemp,
      max: convertedMaxTemp,
      interval: chartPreference.temperature_grid_size,
      splitLine: {
        show: true,
        lineStyle: {
          color: '#e0e0e0',
        },
      },
      axisLabel: {
        formatter: (value) => `${value} ${cycleMeasurementSymbol}`,
        color: '#000',
        customValues,
      },
      axisTick: {
        show: true,
        customValues,
      },
      triggerEvent: false,
    };

    if (chartPreference.isDefaultTemplate) {
      yAxis.min = (value) => applyYAxisPadding(value.min, { min: convertedMinTemp, max: convertedMaxTemp }, 'min');
      yAxis.max = (value) => applyYAxisPadding(value.max, { min: convertedMinTemp, max: convertedMaxTemp }, 'max');

      yAxis.axisLabel = {
        ...yAxis.axisLabel,
        formatter: (value) => `${value}`,
      };

      delete yAxis.interval;
    };

    const chartOptions: EChartsOption = {
      tooltip: [
        {
          id: 'one',
          trigger: 'axis',
          position(point, _, __, ___, size) {
            const { contentSize, viewSize } = size;
            const [x, y] = point;
            const [contentWidth, contentHeight] = contentSize;
            const [viewWidth, viewHeight] = viewSize;

            const moveLeft = x + contentWidth >= viewWidth;
            const moveUp = y + contentHeight >= viewHeight;

            const adjustedX = moveLeft ? x - contentWidth : x;
            const adjustedY = moveUp ? y - contentHeight : y;

            return [adjustedX, adjustedY];
          },
          formatter: function (params) {
            const date = DateFormatter({ date: new Date(params[0].axisValueLabel), format: DateFormats['dd-MMM yyyy HH:mm:ss a'] })
            let tooltipContent = `${date}<br/>`;

            params.forEach((item) => {
              const seriesName = item.seriesName;
              const value = item.data.at(1);
              const data = value ? `${value} ${cycleMeasurementSymbol}` : '';

              if (seriesName === SET_POINT_NAME) {
                tooltipContent += `${item.marker} ${SET_POINT_NAME} (${t('iot.setpoint')}): ${data}<br/>`;
                return;
              }

              const seriesEntity = item.seriesName.split(CHARACTER_SEPARATOR_IOT_KEY_NAME);
              if (seriesEntity.length < 2) {
                tooltipContent += `${item.marker} ${escapeHtml(seriesName)}: ${data}<br/>`;
                return;
              }
              const controllerSerialNumber = seriesEntity[0];
              const deviceNameData = seriesEntity[1].split(CHARACTER_SEPARATOR_DEVICE_NAME);
              const deviceType = deviceNameData[0];
              const deviceNumber = deviceNameData[1];
              const formattedControllerName = cycleControllersName[controllerSerialNumber];
              const formattedDevice = DEVICE_NAME[deviceType] + CHARACTER_SEPARATOR_DEVICE_NAME + deviceNumber;

              const finishedDeviceName = nameDevices[controllerSerialNumber + CHARACTER_SEPARATOR_IOT_KEY_NAME + seriesEntity[1]] || formattedDevice;
              tooltipContent += `${item.marker} ${escapeHtml(finishedDeviceName)}\t(${escapeHtml(formattedControllerName)}): ${data} <br/>`;
            });

            return tooltipContent;
          },
        },
      ],
      legend: {
        show: false,
      },
      grid: {
        show: true,
        top: `${CHART_INDENTS.GRID.TOP}px`,
        left: `${CHART_INDENTS.GRID.LEFT}px`,
        right: `${CHART_INDENTS.GRID.RIGHT}px`,
        bottom: `${CHART_INDENTS.GRID.BOTTOM}px`,
        borderColor: '#000',
      },
      xAxis: [
        {
          id: X_AXIS.BOTTOM.ID,
          type: 'time',
          position: 'bottom',
          axisTick: {
            customValues: generateTimeAxisValues(min, max, chartPreference.time_grid_side),
          },
          axisLabel: {
            ...AXIS_CONFIG.TIME.BOTTOM,
            customValues: subsampleAxisValues(generateTimeAxisValues(min, max, chartPreference.time_grid_side)),
          },
          splitLine: AXIS_CONFIG.SPLIT_LINE,
        },
        {
          id: X_AXIS.TOP.ID,
          type: 'time',
          position: 'top',
          axisTick: {
            customValues: generateDayAxisValues(min, max),
          },
          axisLabel: {
            show: true,
            ...AXIS_CONFIG.TIME.TOP,
            customValues: generateDayAxisValues(min, max),
          },
          splitLine: AXIS_CONFIG.SPLIT_LINE,
        },
      ],
      yAxis,
      animationDurationUpdate: 0,
      dataZoom,
      series: series.filter((item) => !channelsToHide.includes(String(item.name))),
    };

    return chartOptions;
  };

  const generateCycle = (cycleComponents: CycleComponents) => {
    const chartInstance = chartRef.current?.getEchartsInstance() ?? null;
    const dataZoom = chartInstance ? chartInstance.getOption().dataZoom : null;

    const chartOptions = getOptions({ ...cycleComponents, currentDataZoom: dataZoom as DataZoomComponentOption[] });

    const chartSeries = chartOptions.series;

    chartOptions.series = chartSeries;

    return chartOptions;
  };
  /*========================Options===============================*/

  const checkChartZone = (event: any) => {
    const chartContainer = document.querySelector('.c-cycle-chart-preview__container') as HTMLElement;
    const chartElement = document.querySelector('.c-cycle-chart-preview__inner') as HTMLElement;

    const rect = chartContainer.getBoundingClientRect();
    const chartElementRect = chartElement.getBoundingClientRect();

    const topPart = window.getComputedStyle(chartContainer, '::before');
    const bottomPart = window.getComputedStyle(chartContainer, '::after');
    const leftPart = window.getComputedStyle(chartElement, '::before');
    const rightChartElement = window.getComputedStyle(chartElement, '::after');

    if (leftPart && event.offsetX <= CHART_INDENTS.GRID.LEFT) {
      return false;
    }

    if (rightChartElement && event.clientX >= rect.right - CHART_INDENTS.GRID.RIGHT) {
      return false;
    }

    if (topPart && event.offsetY <= CHART_INDENTS.GRID.TOP) {
      return false;
    }

    if (bottomPart && event.clientY >= chartElementRect.bottom - CHART_INDENTS.GRID.BOTTOM) {
      return false;
    }

    return true;
  }

  const areObjectsClose = (coordinateOne: Coordinate, coordinateTwo: Coordinate, tolerance: number = 1) =>
    Math.abs(coordinateOne.x - coordinateTwo.x) <= tolerance && Math.abs(coordinateOne.y - coordinateTwo.y) <= tolerance;

  const viewCycleCreation = (event: any, chart: any) => {
    const isClickZone = checkChartZone(event);

    const currentDataEvent = { ...dataEvent.current };
    dataEvent.current = null;

    if (!isClickZone) {
      return;
    }

    const isCurrentPoint = areObjectsClose(
      {
        x: currentDataEvent.pointerdown.offsetX,
        y: currentDataEvent.pointerdown.offsetY,
      },
      {
        x: event.offsetX,
        y: event.offsetY,
      }
    )

    if ((currentDataEvent.zoom?.dataZoomId === DATA_ZOOM_ID.SLIDER_X
      || currentDataEvent.zoom?.dataZoomId === DATA_ZOOM_ID.SLIDER_Y)
      && !isCurrentPoint) {
      return false;
    }

    if (Array.isArray(currentDataEvent.zoom) && !isCurrentPoint) {
      return false;
    }

    createNote(event, chart)
  }

  const updateVisibleData = (chartEvent) => {
    const start = chartEvent.batch ? chartEvent.batch[0].start : chartEvent.start;
    const end = chartEvent.batch ? chartEvent.batch[0].end : chartEvent.end;

    const newVisibleData = getVisibleData({ startPercent: start, endPercent: end });

    const chartInstance = chartRef.current.getEchartsInstance();
    const currentOptions = chartInstance.getOption();

    currentOptions.series = currentOptions.series.map((seriesItem) => ({
      ...seriesItem,
      data: newVisibleData.map((d) => [d.time, d[seriesItem.id]]),
    }));

    chartInstance.setOption(currentOptions, true);
  }

  const handleDataZoom = (chartEvent, chart) => {
    if (chartEvent.dataZoomId === DATA_ZOOM_ID.SLIDER_X) {
      const time = chart?.getOption()?.series[0]?.data?.[0]?.[0] ?? new Date().getFullYear();
      setCurrentYear(time);
    }
    if (chartEvent.dataZoomId === DATA_ZOOM_ID.SLIDER_X || chartEvent.batch?.length) {
      updateVisibleData(chartEvent)
    }

    const zoomData = (chartEvent?.batch) ? chartEvent.batch : chartEvent;
    updateDataEvent(zoomData, 'zoom');

    const yAxisComponent = chart.getModel().getComponent('yAxis');

    if (!yAxisComponent) return;

    const yScale = yAxisComponent.axis.scale;
    const [currentMin, currentMax] = yScale._extent;

    const newCustomValues = generateTemperatureArray(
      currentMin,
      currentMax,
      chartPreference.temperature_grid_size,
      zoomData
    );

    chart.setOption({
      yAxis: {
        axisLabel: {
          customValues: newCustomValues,
        },
        axisTick: {
          customValues: newCustomValues,
        }
      }
    });
  };

  const updateDataEvent = (data: any, name: keyof DataEvent) => {
    dataEvent.current = { ...dataEvent.current, [name]: data };
  }

  const onChartReady = (chart: EChartsInstance) => {
    if (currentYear === null) {
      const time = chart?.getOption()?.series[0]?.data?.[0]?.[0] ?? new Date().getFullYear();
      setCurrentYear(time);
    }

    chart.on('click', editNote);
    chart.on('dataZoom', (event) => {
      setDataZoom(event);
      chart.dispatchAction({ type: 'hideTip' });
      handleDataZoom(event, chart)
      chart.dispatchAction({ type: 'showTip' });
    });
    chart.getDom().addEventListener('pointerdown', (event) => updateDataEvent(event, 'pointerdown'));
    chart.getDom().addEventListener('pointermove', (event) => updateDataEvent(event, 'pointermove'));
    chart.getDom().addEventListener('click', (event) => viewCycleCreation(event, chart));
    chart.on('legendselectchanged', (params) => {
      setLegendData((oldValue) => ({ ...oldValue, selected: params.selected }));
    });
    chart.on('legendScroll', (params) => {
      setLegendData((oldValue) => ({ ...oldValue, scrollDataIndex: params.scrollDataIndex }));
    });
  };

  const getOptionsData = () => {
    const currentOptions = generateCycle(cycleComponents);

    if (chartRef.current) {
      if (legendData.scrollDataIndex !== null) {
        currentOptions.legend = { ...currentOptions.legend, scrollDataIndex: legendData.scrollDataIndex };
      }

      if (legendData.selected !== null) {
        currentOptions.legend = { ...currentOptions.legend, selected: legendData.selected };
      }

      const chartInstance = chartRef.current.getEchartsInstance();
      chartInstance.setOption(currentOptions as any, true);
    }

    return currentOptions;
  };

  return (
    <>
      <div className='c-cycle-chart-preview z-0 relative h-full'>
        <div className={`c-cycle-chart-preview__container h-full flex-auto ${CLASS_NAME_SPACE.BOTTOM} ${CLASS_NAME_SPACE.TOP}`}>
          <div className={`c-cycle-chart-preview__inner h-full ${CLASS_NAME_SPACE.LEFT} ${CLASS_NAME_SPACE.RIGHT}`}>
            <span className='font-semibold text-[12px] left-[6px] top-[42px] relative'>
              {chartPreference.start_timestamp && formatYearDayMonthShort(Number(chartPreference.start_timestamp))}
            </span>
            <ReactECharts
              onChartReady={onChartReady}
              ref={chartRef}
              option={getOptionsData()}
              style={{ height: '100%', width: '100%', }}
              className='c-cycle-chart-preview__chart echart'
              showLoading={isLoading}
              loadingOption={{
                text: t('labels.loading'),
                color: '#EF8541',
                zlevel: 0
              }}
            />
          </div>
        </div>
      </div>

      {chartRef?.current &&
        <ListLegendsCycle
          list={getSerIdWithColor()}
          cycleControllersName={cycleControllersName}
          nameDevices={nameDevices}
          channelsToHide={channelsToHide}
        />
      }
    </>
  );
};

export default CycleChartPreviewV2;
